import { useCallback, useEffect, useRef, useState } from "react";
import { useGetService } from "./useGetService";
import { API } from "../constants";
import dayjs from "dayjs";
import { getUserData } from "../shared/utils";

interface UseArrivalDepartureDataParams {
  startDate: Date;
  endDate: Date;
  selectedTab: string;
  brand?: string;
  destinationCity?: string;
  empId?: string;
}

const tabMap: Record<string, string> = {
  ARRIVALS: "ARRIVAL",
  DEPARTURE: "DEPARTURE",
};

const formatDateToAPI = (date: Date): string =>
  dayjs(date).format("YYYY-MM-DD");

const buildQueryParams = ({
  startDate,
  endDate,
  selectedTab,
  brand,
  destinationCity,
  empId,
}: UseArrivalDepartureDataParams): string => {
  const params = new URLSearchParams();

  params.append("src", tabMap[selectedTab] || selectedTab);
  params.append("startDate", formatDateToAPI(startDate));
  params.append("endDate", formatDateToAPI(endDate));

  if (brand) params.append("brand", brand);
  if (empId) params.append("EmpId", empId);
  if (destinationCity) params.append("destinationCity", destinationCity);

  return `${API.ADMIN.LIST_ARRIVALS_AND_DEPARTURE}?${params.toString()}`;
};

export const useArrivalDepartureData = (
  params: UseArrivalDepartureDataParams
) => {
  const prevTabRef = useRef<string | null>(tabMap[params.selectedTab] || null);
  const hasCalledInitial = useRef(false);
  const urlRef = useRef("");

  const { response, refetch, error, detailedError } = useGetService(undefined);

  const [isLoading, setIsLoading] = useState(true); // first load + manual apply + tab change
  const [isRefreshing, setIsRefreshing] = useState(false); // only auto refresh

  const buildUrl = useCallback(() => {
    const airportServicedByVendor = sessionStorage.getItem(
      "airportServicedByVendor"
    );
    const user = getUserData();
    const isSuperAdmin = user?.["custom:roleName"] === "superAdmin";
    return buildQueryParams({
      ...params,
      destinationCity: isSuperAdmin
        ? params.destinationCity
        : airportServicedByVendor || undefined,
    });
  }, [params]);

  // ✅ Initial call
  useEffect(() => {
    if (!hasCalledInitial.current) {
      const initialUrl = buildUrl();
      urlRef.current = initialUrl;
      hasCalledInitial.current = true;
      setIsLoading(true);
      refetch(initialUrl).finally(() => setIsLoading(false));
    }
  }, [buildUrl, refetch]);

  // ✅ Tab change
  useEffect(() => {
    const mappedTab = tabMap[params.selectedTab] || params.selectedTab;
    if (hasCalledInitial.current && prevTabRef.current !== mappedTab) {
      const newUrl = buildUrl();
      urlRef.current = newUrl;
      prevTabRef.current = mappedTab;
      setIsLoading(true);
      refetch(newUrl).finally(() => setIsLoading(false));
    }
  }, [params.selectedTab, buildUrl, refetch]);

  // ✅ Manual Apply
  const handleSearchApply = useCallback(() => {
    const newUrl = buildUrl();
    urlRef.current = newUrl;
    setIsLoading(true);
    refetch(newUrl).finally(() => setIsLoading(false));
  }, [buildUrl, refetch]);

  // ✅ Auto Refresh every 15s (show refreshing text instead of loader)
  useEffect(() => {
    const interval = setInterval(() => {
      if (urlRef.current) {
        setIsRefreshing(true);
        refetch(urlRef.current).finally(() => setIsRefreshing(false));
      }
    }, 30 * 60 * 1000); // 30 minutes in ms

    return () => clearInterval(interval);
  }, []);

  return {
    response,
    isLoading, // true for first load, tab change, manual apply
    refreshingText: isRefreshing ? "Refreshing dashboard…" : "", // only auto refresh
    handleSearchApply,
    error,
    detailedError,
  };
};
